/**
 * Comparador del contrato de grilla, igual al de la API (Mongo con `collation { locale: 'es',
 * strength: 1 }`): texto en español sin mayúsculas ni tildes y con la ñ como letra propia; números
 * como números, fechas por instante y `false < true`; vacío (`null` o ausente) antes que todo, y
 * `''` es texto. Las filas desempatan por `spec.key`; las claves exactas de grupos y facets que el
 * comparador da por iguales quedan en orden de código (UTF-16).
 */
import type { GridFieldType, GridSort, GridSpec } from './grid-query';

const collator = new Intl.Collator('es', { sensitivity: 'base' });

export const isEmpty = (value: unknown): value is null | undefined =>
  value === null || value === undefined;

/** Instante en milisegundos de un `Date` o de un texto ISO; `NaN` si no es fecha. */
export function toInstant(value: unknown): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string' || typeof value === 'number') return new Date(value).getTime();
  return NaN;
}

/** Valor de un campo, también con ruta de puntos (`actor.name`). */
export function getValue(row: unknown, field: string): unknown {
  const read = (value: unknown, part: string) =>
    isEmpty(value) ? undefined : (value as Record<string, unknown>)[part];
  return field.includes('.') ? field.split('.').reduce(read, row) : read(row, field);
}

function codeUnitOrder(a: unknown, b: unknown): number {
  const left = String(a);
  const right = String(b);
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

/** Compara dos valores de un campo de tipo `type`, en orden ascendente. */
export function compareValues(a: unknown, b: unknown, type?: GridFieldType): number {
  const left = type === 'date' && !isEmpty(a) ? toInstant(a) : a;
  const right = type === 'date' && !isEmpty(b) ? toInstant(b) : b;
  // Una fecha que no se puede leer cuenta como vacía.
  const leftEmpty = isEmpty(left) || Number.isNaN(left);
  const rightEmpty = isEmpty(right) || Number.isNaN(right);
  if (leftEmpty || rightEmpty) {
    if (leftEmpty === rightEmpty) return 0;
    return leftEmpty ? -1 : 1;
  }
  switch (type) {
    case 'number':
    case 'date':
      return Math.sign((left as number) - (right as number));
    case 'boolean':
      return Number(left) - Number(right);
    default:
      return collator.compare(String(left), String(right));
  }
}

/** Compara claves exactas de grupos o de facets. */
export function compareKeys(a: unknown, b: unknown, type?: GridFieldType, dir = 'asc'): number {
  const order = compareValues(a, b, type);
  if (order) return dir === 'desc' ? -order : order;
  return codeUnitOrder(a, b);
}

/** Desempate por la clave de la fila: números como números; el resto, como texto. */
function compareRowKeys(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return Math.sign(a - b);
  return collator.compare(String(a), String(b)) || codeUnitOrder(a, b);
}

/** `sort` con el desempate final por `spec.key` ascendente (si no estaba ya). */
function sortWithKey(sort: GridSort[], spec: GridSpec): GridSort[] {
  const key = spec.key || '_id';
  return sort.some(({ field }) => field === key) ? sort : [...sort, { field: key, dir: 'asc' }];
}

/** Comparador de filas para `sort` (ya normalizado), con el desempate de `spec.key`. */
export function rowComparator(
  sort: GridSort[],
  spec: GridSpec,
): (a: unknown, b: unknown) => number {
  const key = spec.key || '_id';
  const steps = sortWithKey(sort, spec).map(({ field, dir }) => ({
    field,
    sign: dir === 'desc' ? -1 : 1,
    compare:
      field === key && !spec.fields[field]
        ? compareRowKeys
        : (a: unknown, b: unknown) => compareValues(a, b, spec.fields[field]?.type),
  }));
  return (rowA, rowB) => {
    for (const { field, sign, compare } of steps) {
      const order = sign * compare(getValue(rowA, field), getValue(rowB, field));
      if (order !== 0) return order;
    }
    return 0;
  };
}

/** Clave exacta de un valor para agrupar: vacío → `null`; fecha → instante en ISO. */
export function groupKey(value: unknown, type?: GridFieldType): unknown {
  if (isEmpty(value)) return null;
  if (type !== 'date') return value;
  const instant = toInstant(value);
  return Number.isNaN(instant) ? null : new Date(instant).toISOString();
}
